import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Bell, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './Button';
import { useAuth } from '../contexts/AuthContext';
import { db, isFirebaseEnabled } from '../lib/firebase';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc, limit } from 'firebase/firestore';
interface Notification {
  id: string;
  type: 'quest' | 'collaboration' | 'subscription' | 'achievement' | 'focus' | 'system';
  title: string;
  message: string;
  read: boolean;
  createdAt: any;
  actionUrl?: string;
  icon?: string;
}
interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function NotificationsModal({
  isOpen,
  onClose
}: NotificationsModalProps) {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!isOpen || !user) return;
    if (!isFirebaseEnabled || !db) {
      // Mock notifications for local mode
      setNotifications([{
        id: '1',
        type: 'quest',
        title: 'Quest Completed! 🎉',
        message: 'You completed "Finish project proposal"',
        read: false,
        createdAt: new Date(Date.now() - 3600000),
        icon: '🎯'
      }, {
        id: '2',
        type: 'collaboration',
        title: 'New Collaboration Invite',
        message: 'Alex invited you to collaborate on "Team Meeting Prep"',
        read: false,
        createdAt: new Date(Date.now() - 7200000),
        icon: '👥'
      }, {
        id: '3',
        type: 'achievement',
        title: 'Achievement Unlocked!',
        message: 'You earned "Quest Master" - Complete 10 quests',
        read: true,
        createdAt: new Date(Date.now() - 86400000),
        icon: '🏆'
      }]);
      setLoading(false);
      return;
    }
    // Real-time Firebase subscription - get latest 5 notifications
    const notificationsRef = collection(db, 'notifications');
    const q = query(notificationsRef, where('userId', '==', user.uid), orderBy('createdAt', 'desc'), limit(5));
    const unsubscribe = onSnapshot(q, snapshot => {
      const notificationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Notification[];
      setNotifications(notificationsData);
      setLoading(false);
    });
    return unsubscribe;
  }, [isOpen, user]);
  const unreadCount = notifications.filter(n => !n.read).length;
  const handleMarkAsRead = async (notificationId: string) => {
    if (!isFirebaseEnabled || !db) {
      setNotifications(prev => prev.map(n => n.id === notificationId ? {
        ...n,
        read: true
      } : n));
      return;
    }
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  const handleViewAll = () => {
    onClose();
    navigate('/notifications');
  };
  const getNotificationIcon = (type: string) => {
    const icons = {
      quest: '🎯',
      collaboration: '👥',
      subscription: '👑',
      achievement: '🏆',
      focus: '🧘',
      system: '🔔'
    };
    return icons[type as keyof typeof icons] || '🔔';
  };
  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };
  return <AnimatePresence data-id="element-384">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-end z-50 p-6 pt-24" onClick={onClose} data-id="element-385">
          <motion.div initial={{
        opacity: 0,
        x: 100,
        scale: 0.95
      }} animate={{
        opacity: 1,
        x: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        x: 100,
        scale: 0.95
      }} className="bg-white dark:bg-[#252936] rounded-3xl shadow-2xl w-full max-w-md max-h-[600px] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-386">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-387">
              <div className="flex items-center justify-between mb-2" data-id="element-388">
                <div className="flex items-center gap-3" data-id="element-389">
                  <div className="w-10 h-10 bg-game-purple/20 rounded-full flex items-center justify-center" data-id="element-390">
                    <Bell className="w-5 h-5 text-game-purple" data-id="element-391" />
                  </div>
                  <div data-id="element-392">
                    <h2 className="text-xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-393">
                      Notifications
                    </h2>
                    {unreadCount > 0 && <p className="text-xs text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-394">
                        {unreadCount} unread
                      </p>}
                  </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" data-id="element-395">
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-396" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto max-h-[400px]" data-id="element-397">
              {loading ? <div className="flex items-center justify-center py-12" data-id="element-398">
                  <div className="w-8 h-8 border-4 border-game-purple border-t-transparent rounded-full animate-spin" data-id="element-399"></div>
                </div> : notifications.length === 0 ? <div className="text-center py-12 px-6" data-id="element-400">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-401">
                    <Bell className="w-8 h-8 text-gray-400" data-id="element-402" />
                  </div>
                  <p className="font-fredoka font-bold text-game-dark dark:text-white mb-1" data-id="element-403">
                    No notifications
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-404">
                    You're all caught up!
                  </p>
                </div> : <div className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-405">
                  {notifications.map(notification => <motion.div key={notification.id} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer ${!notification.read ? 'bg-game-bg-purple/30 dark:bg-purple-900/10' : ''}`} onClick={() => handleMarkAsRead(notification.id)} data-id="element-406">
                      <div className="flex gap-3" data-id="element-407">
                        <div className="flex-shrink-0" data-id="element-408">
                          <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center text-xl" data-id="element-409">
                            {notification.icon || getNotificationIcon(notification.type)}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-410">
                          <div className="flex items-start justify-between gap-2 mb-1" data-id="element-411">
                            <h3 className="font-fredoka font-bold text-sm text-game-dark dark:text-white" data-id="element-412">
                              {notification.title}
                            </h3>
                            {!notification.read && <div className="w-2 h-2 bg-game-coral rounded-full flex-shrink-0 mt-1" data-id="element-413"></div>}
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 font-quicksand mb-1" data-id="element-414">
                            {notification.message}
                          </p>
                          <span className="text-xs text-gray-500 dark:text-gray-500 font-quicksand" data-id="element-415">
                            {formatTime(notification.createdAt)}
                          </span>
                        </div>
                      </div>
                    </motion.div>)}
                </div>}
            </div>

            {/* Footer */}
            {notifications.length > 0 && <div className="p-4 border-t border-gray-200 dark:border-gray-800" data-id="element-416">
                <Button variant="secondary" className="w-full" onClick={handleViewAll} icon={<ExternalLink className="w-4 h-4" data-id="element-418" />} data-id="element-417">
                  View All Notifications
                </Button>
              </div>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}